import React, { useMemo, useCallback } from 'react';

import { TableConfig, ColumnConfig } from '@/components/types/types';

interface UseTableConfigOptions<T> {
  columns: ColumnConfig<T>[];
  currentPage: number;
  itemsPerPage: number;
  sortField?: string | null;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string, order: 'asc' | 'desc') => void;
  enableServerSort?: boolean;
  hidePagination?: boolean;
  getRowStyle?: (item: T) => React.CSSProperties;
}

export const useTableConfig = <T>({
  columns,
  currentPage,
  itemsPerPage,
  sortField,
  sortOrder = 'desc',
  onSortChange,
  enableServerSort = true,
  hidePagination = false,
  getRowStyle,
}: UseTableConfigOptions<T>) => {
  // Handle sort change
  const handleSortChange = useCallback(
    (field: string, order: 'asc' | 'desc') => {
      onSortChange?.(field, order);
    },
    [onSortChange]
  );

  // Table configuration
  const tableConfig: TableConfig<T> = useMemo(
    () => ({
      columns,
      showApiInfo: false,
      enableServerSort,
      enableServerPagination: false,
      stickyHeader: true,
      height: { xs: '400px', sm: '500px', md: '600px' },
      maxHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
      initialPagination: {
        page: currentPage - 1, // Convert to 0-based index
        rowsPerPage: itemsPerPage,
        rowsPerPageOptions: [20, 50, 100],
      },
      initialSort: {
        field: sortField || null,
        order: sortOrder,
      },
      onSortChange: handleSortChange,
      hidePagination,
      getRowStyle,
    }),
    [
      columns,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      handleSortChange,
      enableServerSort,
      hidePagination,
      getRowStyle,
    ]
  );

  return {
    tableConfig,
    handleSortChange,
  };
};
